import { useInfiniteQuery } from 'react-query';
import type { EisFlaggedPersonnelResponse } from '../_types';
import {
  performanceReportingApiUrl,
  fetcher,
  handleError,
  ServerErrorResponse,
  createParamString,
} from 'data_sources/constants';

export type EisFlaggedPersonnelParams = {
  nextToken?: string;
} & Record<string, unknown>;

export const getEisFlaggedPersonnel = async (
  ruleId: number,
  params: EisFlaggedPersonnelParams = {},
  signal?: AbortSignal,
  onError?: (errors: ServerErrorResponse) => void,
): Promise<EisFlaggedPersonnelResponse> => {
  try {
    const baseUrl = `${performanceReportingApiUrl()}/performance/eis/v1/rules/${ruleId}/flagged-personnel`;
    const queryParams = createParamString(params);
    const fullUrl = queryParams ? `${baseUrl}?${queryParams}` : baseUrl;

    const response = await fetcher({
      url: fullUrl,
      method: 'GET',
      options: { signal },
    });

    if (response.errorDetails) {
      if (onError) onError(response);
      throw new Error(JSON.stringify(response));
    }

    return response;
  } catch (error: unknown) {
    if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
      onError(error as ServerErrorResponse);
    } else if (onError && error instanceof Error) {
      handleError(error, { onError });
    }
    throw error;
  }
};

export const useEisFlaggedPersonnel = (
  ruleId: number,
  params: EisFlaggedPersonnelParams = {},
  options = {},
  onError?: (errors: ServerErrorResponse) => void,
) => {
  return useInfiniteQuery<EisFlaggedPersonnelResponse, ServerErrorResponse>(
    ['eisFlaggedPersonnel', ruleId, JSON.stringify(params)],
    async ({ pageParam, signal }) => {
      const combinedParams = {
        ...params,
        nextToken: pageParam,
      };
      return getEisFlaggedPersonnel(ruleId, combinedParams, signal, onError);
    },
    {
      keepPreviousData: true,
      getNextPageParam: (lastPage) => lastPage.nextToken || undefined,
      enabled: !!ruleId,
      ...options,
    },
  );
};
